import type { ExportableReport, ReportDimension, ReportSnapshotStatus, ReportSnapshotType } from '@/api/reports-types'
import type { Branch } from '@/api/types'
import { branchLabel } from '@/lib/branch'
import type { PermissionCode } from '@/lib/permissions'

import { REPORTS_PERMISSIONS } from './permissions'

/** The browser's date, for field defaults and ceilings only — never to decide a business date. */
export const today = (): string => new Date().toISOString().slice(0, 10)

/** The first day of the month `isoDate` falls in, for defaulting a report's range. */
export const startOfMonth = (isoDate: string): string => `${isoDate.slice(0, 7)}-01`

/** A fraction in 0–1 as a percentage, or a dash for a figure the report did not compute. */
export const formatRatio = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(1)}%`

/** Stands in for "no filter" — a select option cannot itself carry an empty value. */
export const ANY = '__any__'

/**
 * A row's `branchId` against the branches this user can reach, for a friendlier column than a raw
 * identifier. The reach list is a floor rather than a directory — see `useReportsBranchReach` — so
 * a branch outside it still renders, honestly, as its bare identifier rather than a guessed name.
 */
export function resolveBranchLabel(branches: Branch[], branchId: string): string {
  const match = branches.find((branch) => branch.id === branchId)
  return match ? branchLabel(match) : `Branch ${branchId}`
}

export const DIMENSION_LABEL: Record<ReportDimension, string> = {
  Institution: 'The whole institution',
  Branch: 'Branch',
  LoanOfficer: 'Loan officer',
  Group: 'Group',
  LoanProduct: 'Loan product',
}

/** The dimensions worth offering on a summary screen — every one the endpoint accepts. */
export const REPORT_DIMENSIONS: ReportDimension[] = [
  'Institution',
  'Branch',
  'LoanOfficer',
  'Group',
  'LoanProduct',
]

/**
 * Which `ExportableReport` code the export endpoint answers to for the performance screen's current
 * dimension, in every format it serves. Every dimension has one now — the three that used to return
 * null were the module's second capability gap, closed on the server rather than papered over here.
 */
export function performanceExportReport(dimension: ReportDimension): ExportableReport {
  switch (dimension) {
    case 'LoanOfficer':
      return 'OfficerPerformance'
    case 'Group':
      return 'GroupPerformance'
    case 'Branch':
      return 'BranchPerformance'
    case 'LoanProduct':
      return 'LoanProductPerformance'
    case 'Institution':
      return 'InstitutionPerformance'
  }
}

/** A human label for each report a snapshot can freeze — the same six {@link ReportSnapshotType} values. */
export const SNAPSHOT_TYPE_LABEL: Record<ReportSnapshotType, string> = {
  PortfolioAtRisk: 'Portfolio at risk',
  OfficerPerformance: 'Performance',
  TrialBalance: 'Trial balance',
  GroupHealth: 'Group health',
  CollectionsRegister: 'Collections register',
  DisbursementsRegister: 'Disbursements register',
}

/** Badge tone for a snapshot's status — a restated figure is neutral, not a warning. */
export const SNAPSHOT_STATUS_TONE: Record<ReportSnapshotStatus, 'success' | 'warning' | 'neutral'> = {
  Complete: 'success',
  Partial: 'warning',
  Superseded: 'neutral',
}

/**
 * What the reporting module is catalogued to do and cannot yet, from this application or from the
 * API it calls — the same honesty `LEDGER_CAPABILITY_GAPS` keeps for the ledger. Read before
 * assuming a blank result means an empty book rather than a capability that is not there yet.
 */
export interface ReportsCapabilityGap {
  title: string
  permission: PermissionCode
  detail: string
}

/*
 * Three entries retired on one day, each by the thing it named: guarantee call-ins now appear on
 * the register (listed and separately totalled, deliberately never counted as collected);
 * performance exports in every dimension; and bulk export works for a holder of
 * reports.export.bulk through the same buttons. The fourth shrank — the bureau extract and the
 * prudential return turned out to be published all along under /compliance (the pinned contract
 * was stale, not the server) and now have a screen; only the statement generator remains unserved.
 */
export const REPORTS_CAPABILITY_GAPS: ReportsCapabilityGap[] = [
  {
    title: 'No batch statement generator is published',
    permission: REPORTS_PERMISSIONS.statementGenerate,
    detail:
      'reports.statement.generate is a catalogued permission with no backing endpoint — there is nothing for this client to call. Individual savings and loan statements exist on each account under their own view permissions; what is missing is the periodic run that produces every customer’s statement in one pass, which the reconciliation cadence in the design depends on.',
  },
]
